import React, { memo } from 'react';
import { Card, CardContent, Box, Typography, useTheme } from '@mui/material';
import { SPACING } from '../../../theme';
import PropTypes from 'prop-types';
import { getCardHoverStyles } from '../../common/cardHoverStyles';
import TemplateFeedbackDisplay from './TemplateFeedbackDisplay';
import {
  CardHeader,
  PromptPreview,
  TemplateTags,
  CompactCardActions,
} from './CardCompactComponents';

/**
 * Compact Template Card with Progressive Disclosure
 *
 * Features:
 * 1. Collapsible prompt preview (show more/less)
 * 2. Secondary actions grouped in menu
 * 3. Better visual hierarchy
 * 4. Reduced information density
 * 5. Essential info visible, details on demand
 */

const CardCompact = ({
  template,
  analytics,
  onEdit,
  onExecute,
  onOpenVersions,
  onOpenExecutions,
  onDelete,
  onCreateExperiment,
  onViewAnalytics,
  onOpenFeedback,
  hoverShadow,
  sx,
  showAnalytics = true,
}) => {
  const theme = useTheme();
  const promptText = template.template || '';

  // Get common hover styles (matches template version select MenuItem hover effect)
  const hoverStyles = getCardHoverStyles(theme);

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        // Use common hover styles
        ...hoverStyles,
        // Allow custom hover shadow overrides if provided
        '&:hover': {
          ...hoverStyles['&:hover'],
          ...hoverShadow,
        },
        ...sx,
      }}
    >
      <CardContent
        sx={{
          p: SPACING.md,
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header: Title, Version, Rating - Essential Info Only */}
        <CardHeader
          template={template}
          analytics={analytics}
          showAnalytics={showAnalytics}
        />

        {/* Description - Single line, truncated */}
        {template.template_description || template.description ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: SPACING.sm,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {template.template_description || template.description}
          </Typography>
        ) : null}

        {/* Prompt Preview - Collapsible */}
        <PromptPreview promptText={promptText} />

        {/* Feedback Display - Compact */}
        {template.feedback && (
          <Box sx={{ mb: SPACING.sm }}>
            <TemplateFeedbackDisplay
              feedback={template.feedback}
              analytics={template.feedbackAnalytics}
              summary={template.feedbackSummary}
              loading={template.feedbackLoading}
              compact={true}
            />
          </Box>
        )}

        {/* Tags - Compact, max 3 visible */}
        <TemplateTags template={template} />

        {/* Spacer to push actions to bottom */}
        <Box sx={{ flexGrow: 1 }} />
      </CardContent>

      {/* Actions - Primary actions visible, secondary in menu */}
      <CompactCardActions
        onEdit={onEdit}
        onExecute={onExecute}
        onOpenVersions={onOpenVersions}
        onOpenExecutions={onOpenExecutions}
        onDelete={onDelete}
        onCreateExperiment={onCreateExperiment}
        onViewAnalytics={onViewAnalytics}
        onOpenFeedback={onOpenFeedback}
      />
    </Card>
  );
};

CardCompact.propTypes = {
  template: PropTypes.object.isRequired,
  analytics: PropTypes.object,
  onEdit: PropTypes.func.isRequired,
  onExecute: PropTypes.func.isRequired,
  onOpenVersions: PropTypes.func,
  onOpenExecutions: PropTypes.func,
  onDelete: PropTypes.func,
  onCreateExperiment: PropTypes.func,
  onViewAnalytics: PropTypes.func,
  onOpenFeedback: PropTypes.func,
  hoverShadow: PropTypes.object,
  sx: PropTypes.object,
  showAnalytics: PropTypes.bool,
};

export default memo(CardCompact);
